"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import { GiBrain } from "react-icons/gi";
import {
  HiOutlineBookOpen,
  HiOutlineMicrophone,
  HiOutlinePencilAlt,
  HiOutlineVolumeUp,
} from "react-icons/hi";
import { MdOutlineChat } from "react-icons/md";
import { IoGameControllerOutline } from "react-icons/io5";
import { TbCategory2 } from "react-icons/tb";
import ConfirmPopUp from "../../popup/confirmPopUp";
import StatusPopUp from "../../popup/statusPopUp";
import { buildGameSuccessStatus } from "../shared/gameStatusMessages";
import { areMatchingItemsSaveReady } from "../shared/mediaValidation";
import matchingAPI from "../../../services/matching/matchingAPI";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import { collectPersistedIdsFromApiRows } from "../shared/serverPersistedIds";
import { MEDIA_BTN_DELETE_MD } from "../shared/mediaIconButtonClasses";
import MediaEditGroup from "../shared/MediaEditGroup";
import { GamePreviewModal } from "../shared/GamePreviewModal";
import {
  createMatchingItemsGameSnapshot,
  getCanGamePreview,
  getShouldConfirmLeaveGameEditor,
  patchSavedMatchingItemsSnapshotAfterItemRemoved,
  patchSavedMatchingItemsSnapshotAfterMediaDeleted,
  PREVIEW_BUTTON_DISABLED_TITLE,
  GAME_PREVIEW_BUTTON_CLASS,
  PREVIEW_UNSAVED_MESSAGE,
  PREVIEW_UNSAVED_TITLE,
  useHasUnsavedGameFormChanges,
} from "../shared/gameFormSnapshot";
import { useReportGameEditorLeaveGuard } from "../shared/GameEditorLeaveGuard";
import {
  createGameFieldErrorPopUp,
  matchingItemFieldTarget,
  type GameStatusPopUpState,
  useGameFieldErrorHighlight,
} from "../shared/fieldErrorHighlight";
import { useTranslations } from "next-intl";

type MatchingItem = {
  id: number;
  no: number;
  keyword: string;
  matchingWord: string;
  imageKeywordFileName: string;
  imageKeywordFile: File | null;
  imageKeywordUrl: string | null;
  audioKeywordFileName: string;
  audioKeywordFile: File | null;
  audioKeywordUrl: string | null;
  imageMatchingFileName: string;
  imageMatchingFile: File | null;
  imageMatchingUrl: string | null;
  audioMatchingFileName: string;
  audioMatchingFile: File | null;
  audioMatchingUrl: string | null;
  hasHint: boolean;
  hintText: string;
  hintImageFileName: string;
  hintImageFile: File | null;
  hintImageUrl: string | null;
  hintAudioFileName: string;
  hintAudioFile: File | null;
  hintAudioUrl: string | null;
};

type FlipCardMediaDeleteField =
  | "sound_keyword"
  | "sound_matching_word"
  | "image_keyword"
  | "image_matching_word";

type PendingMediaDelete = {
  itemId: number;
  field: FlipCardMediaDeleteField;
};

type UploadTarget = {
  key: string;
  file: File;
  fileName: string;
  sectionName: string;
  refId: string;
};

type GameSkills = {
  skill_listening_speaking: boolean;
  skill_lang_nature: boolean;
  skill_reading: boolean;
  skill_writing: boolean;
};

type GameSkillKey = keyof GameSkills;

const DEFAULT_GAME_SKILLS: GameSkills = {
  skill_listening_speaking: false,
  skill_lang_nature: false,
  skill_reading: false,
  skill_writing: false,
};

type GameAudios = {
  audio_question: boolean;
  audio_choice: boolean;
  audio_assistant_voice: boolean;
  audio_background: boolean;
};

type GameAudiosKey = keyof GameAudios;

type StatusToggleDialogContent = {
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  successMessage: string;
};

const DEFAULT_GAME_AUDIOS: GameAudios = {
  audio_question: false,
  audio_choice: false,
  audio_assistant_voice: false,
  audio_background: false,
};

type FlipCardsProps = {
  createdGameData?: {
    id: number | string;
    exercise_name?: string;
    uuid?: string;
    description?: string | null;
    status?: boolean | "true" | "false" | number | string | null;
  } | null;
  initialGameDetail?: {
    game_info?: {
      id?: number | string;
      uuid?: string;
      exercise_name?: string;
      suggestion?: string | null;
      description?: string | null;
      status?: boolean | "true" | "false" | number | string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    };
    questions?: Array<{
      id?: number;
      no?: number;
      keyword?: string;
      question?: string;
      matching_word?: string;
      answer?: string;
      hint?: string | null;
      image_keyword?: string | null;
      sound_keyword?: string | null;
      image_matching_word?: string | null;
      sound_matching_word?: string | null;
      image_hint?: string | null;
      sound_hint?: string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    }>;
  } | null;
  onCompleted?: () => void;
};

const MIN_ITEMS = 2;
const MAX_ITEMS = 5;

function getFileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return "";
  }
  return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
  if (!path) {
    return "";
  }

  const cleanPath = String(path);
  const bySlash = cleanPath.split("/");
  const candidate = bySlash[bySlash.length - 1] || "";
  return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
  if (!path) {
    return null;
  }

  const raw = String(path).trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://thaipilacreate.gforcesolution.com";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

function normalizeSkillBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return null;
}

function normalizeStatus(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

function normalizeExpectedTime(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return "0";
  }
  return String(Math.floor(parsed));
}

function getPublishButtonClass(published: boolean): string {
  const base =
    "cursor-pointer inline-flex h-9 w-[116px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-75";

  if (published) {
    return `${base} bg-[#E6F8EF]`;
  }

  return `${base} bg-[#E0E0E0]`;
}

function renderPublishStatusContent(
  published: boolean,
  labels: {
    publish: string;
    unpublish: string;
  },
  iconSize = 16,
) {
  if (published) {
    return (
      <>
        <span className="inline-flex shrink-0 items-center justify-center">
          <BiWorld size={iconSize} color="#16A34A" />
        </span>
        <span className="text-[#16A34A]">{labels.publish}</span>
      </>
    );
  }

  return (
    <>
      <span className="inline-flex shrink-0 items-center justify-center">
        <BiWorld size={iconSize} color="#3b3b3b" />
      </span>
      <span className="text-[#3b3b3b]">{labels.unpublish}</span>
    </>
  );
}

function getStatusToggleDialogContent(
  currentStatus: boolean,
  labels: {
    confirmPublish: string;
    publishPrompt: string;
    published: string;
    publishedMessage: string;
    confirmUnpublish: string;
    unpublishPrompt: string;
    unpublished: string;
    unpublishedMessage: string;
  },
): StatusToggleDialogContent {
  const nextStatus = !currentStatus;

  if (nextStatus) {
    return {
      confirmTitle: labels.confirmPublish,
      confirmMessage: labels.publishPrompt,
      successTitle: labels.published,
      successMessage: labels.publishedMessage,
    };
  }

  return {
    confirmTitle: labels.confirmUnpublish,
    confirmMessage: labels.unpublishPrompt,
    successTitle: labels.unpublished,
    successMessage: labels.unpublishedMessage,
  };
}

function normalizeGameSkillsOrNull(
  value: Partial<Record<GameSkillKey, unknown>>,
) {
  const listening = normalizeSkillBoolean(value.skill_listening_speaking);
  const langNature = normalizeSkillBoolean(value.skill_lang_nature);
  const reading = normalizeSkillBoolean(value.skill_reading);
  const writing = normalizeSkillBoolean(value.skill_writing);

  if (
    listening === null ||
    langNature === null ||
    reading === null ||
    writing === null
  ) {
    return null;
  }

  return {
    skill_listening_speaking: listening,
    skill_lang_nature: langNature,
    skill_reading: reading,
    skill_writing: writing,
  };
}

function extractGameSkillsFromUnknown(
  value: Partial<Record<GameSkillKey, unknown>>,
): GameSkills {
  const normalized = normalizeGameSkillsOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function normalizeGameAudiosOrNull(
  value: Partial<Record<GameAudiosKey, unknown>>,
) {
  const audioQuestion = normalizeSkillBoolean(value.audio_question);
  const audioChoice = normalizeSkillBoolean(value.audio_choice);
  const audioAssistantVoice = normalizeSkillBoolean(
    value.audio_assistant_voice,
  );
  const audioBackground = normalizeSkillBoolean(value.audio_background);

  if (
    audioQuestion === null ||
    audioChoice === null ||
    audioAssistantVoice === null ||
    audioBackground === null
  ) {
    return null;
  }

  return {
    audio_question: audioQuestion,
    audio_choice: audioChoice,
    audio_assistant_voice: audioAssistantVoice,
    audio_background: audioBackground,
  };
}

function areGameAudiosEqual(left: GameAudios, right: GameAudios) {
  return (
    left.audio_question === right.audio_question &&
    left.audio_choice === right.audio_choice &&
    left.audio_assistant_voice === right.audio_assistant_voice &&
    left.audio_background === right.audio_background
  );
}

function extractGameAudiosFromUnknown(
  value: Partial<Record<GameAudiosKey, unknown>>,
): GameAudios {
  const normalized = normalizeGameAudiosOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_AUDIOS };
}

function createEmptyMatchingItem(id: number, no: number): MatchingItem {
  return {
    id,
    no,
    keyword: "",
    matchingWord: "",
    imageKeywordFileName: "",
    imageKeywordFile: null,
    imageKeywordUrl: null,
    audioKeywordFileName: "",
    audioKeywordFile: null,
    audioKeywordUrl: null,
    imageMatchingFileName: "",
    imageMatchingFile: null,
    imageMatchingUrl: null,
    audioMatchingFileName: "",
    audioMatchingFile: null,
    audioMatchingUrl: null,
    hasHint: false,
    hintText: "",
    hintImageFileName: "",
    hintImageFile: null,
    hintImageUrl: null,
    hintAudioFileName: "",
    hintAudioFile: null,
    hintAudioUrl: null,
  };
}

export default function FlipCards({
  createdGameData,
  initialGameDetail,
  onCompleted,
}: FlipCardsProps) {
  const tFC = useTranslations("flipCards");
  const tMain = useTranslations("main");
  const publishStatusLabels = {
    publish: tFC("publish"),
    unpublish: tFC("unpublish"),
  };
  const statusToggleDialogLabels = {
    confirmPublish: tFC("confirmPublish"),
    publishPrompt: tFC("publishPrompt"),
    published: tFC("published"),
    publishedMessage: tFC("publishedMessage"),
    confirmUnpublish: tFC("confirmUnpublish"),
    unpublishPrompt: tFC("unpublishPrompt"),
    unpublished: tFC("unpublished"),
    unpublishedMessage: tFC("unpublishedMessage"),
  };
  const nextMatchingIdRef = useRef(6);
  const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
  const initialSuggestionRef = useRef("");
  const initialDescriptionRef = useRef("");
  const initialMatchingIdsRef = useRef<Set<number>>(new Set());
  const savedFormSnapshotRef = useRef<ReturnType<
    typeof createMatchingItemsGameSnapshot
  > | null>(null);
  const initialGameSkillsRef = useRef<GameSkills>({ ...DEFAULT_GAME_SKILLS });
  const initialGameAudiosRef = useRef<GameAudios>({ ...DEFAULT_GAME_AUDIOS });

  const [exerciseName, setExerciseName] = useState(
    createdGameData?.exercise_name ?? "",
  );
  const [expectedTime, setExpectedTime] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [pendingDeleteItemId, setPendingDeleteItemId] = useState<number | null>(
    null,
  );
  const [isUpdateMode, setIsUpdateMode] = useState(
    Array.isArray(initialGameDetail?.questions) &&
      initialGameDetail.questions.length > 0,
  );
  const [isPublished, setIsPublished] = useState(
    normalizeStatus(
      initialGameDetail?.game_info?.status ?? createdGameData?.status ?? false,
    ),
  );
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [isStatusConfirming, setIsStatusConfirming] = useState(false);
  const [statusPopUp, setStatusPopUp] = useState<GameStatusPopUpState | null>(
    null,
  );
  const {
    fieldErrorHighlightKey,
    getFieldBorderClass,
    clearFieldErrorHighlight,
    openStatusPopUp,
    closeStatusPopUp,
  } = useGameFieldErrorHighlight();
  const [pendingMediaDelete, setPendingMediaDelete] =
    useState<PendingMediaDelete | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
  const [gameSkills, setGameSkills] = useState<GameSkills>({
    ...DEFAULT_GAME_SKILLS,
  });
  const [gameAudios, setGameAudios] = useState<GameAudios>({
    ...DEFAULT_GAME_AUDIOS,
  });
  const [items, setItems] = useState<MatchingItem[]>([
    createEmptyMatchingItem(1, 1),
    createEmptyMatchingItem(2, 2),
  ]);
  const canAddItem = items.length < MAX_ITEMS;
  const canRemoveItem = items.length > MIN_ITEMS;

  const previewUuid = String(
    initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? "",
  ).trim();

  useEffect(() => {
    setExerciseName(createdGameData?.exercise_name ?? "");
    setIsPublished(normalizeStatus(createdGameData?.status ?? false));
    initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
  }, [createdGameData?.exercise_name, createdGameData?.status]);

  useEffect(() => {
    if (initialGameDetail?.game_info?.id) {
      return;
    }

    const incomingDescription = String(createdGameData?.description ?? "");
    setDescription(incomingDescription);
    initialDescriptionRef.current = incomingDescription;
  }, [createdGameData?.description, initialGameDetail?.game_info?.id]);

  const applyLoadedGameDetail = (
    detail: FlipCardsProps["initialGameDetail"],
  ) => {
    if (!detail?.game_info?.id) {
      const createdDescription = String(createdGameData?.description ?? "");
      setSuggestion("");
      setDescription(createdDescription);
      setExpectedTime("");
      initialSuggestionRef.current = "";
      initialDescriptionRef.current = createdDescription;
      initialMatchingIdsRef.current = new Set();
      initialGameSkillsRef.current = { ...DEFAULT_GAME_SKILLS };
      initialGameAudiosRef.current = { ...DEFAULT_GAME_AUDIOS };
      setGameSkills({ ...DEFAULT_GAME_SKILLS });
      setGameAudios({ ...DEFAULT_GAME_AUDIOS });
      setIsUpdateMode(false);
      savedFormSnapshotRef.current = createMatchingItemsGameSnapshot(
        createdGameData?.exercise_name ?? "",
        "",
        createdDescription,
        { ...DEFAULT_GAME_SKILLS },
        { ...DEFAULT_GAME_AUDIOS },
        [createEmptyMatchingItem(1, 1), createEmptyMatchingItem(2, 2)],
      );
      return;
    }

    const gameInfo = detail.game_info;
    const incomingRows = Array.isArray(detail.questions)
      ? detail.questions
      : [];

    const incomingExerciseName = String(gameInfo.exercise_name ?? "");
    const incomingSuggestion = String(gameInfo.suggestion ?? "");
    const incomingDescription = String(gameInfo.description ?? "");
    const incomingStatus = normalizeStatus(gameInfo.status);

    setExerciseName(incomingExerciseName);
    setSuggestion(incomingSuggestion);
    setDescription(incomingDescription);
    setExpectedTime(
      normalizeExpectedTime(
        (incomingRows[0] as Record<string, unknown> | undefined)?.expected_time,
      ),
    );
    setIsPublished(incomingStatus);
    initialExerciseNameRef.current = incomingExerciseName;
    initialSuggestionRef.current = incomingSuggestion;
    initialDescriptionRef.current = incomingDescription;

    const mappedItems =
      incomingRows.length > 0
        ? incomingRows.map((row, index) => {
            const record = row as Record<string, unknown>;
            const mappedId = Number(record.id ?? index + 1);
            const normalizedId =
              Number.isInteger(mappedId) && mappedId > 0 ? mappedId : index + 1;
            const keywordRaw = String(record.keyword ?? record.question ?? "");
            const matchingRaw = String(
              record.matching_word ?? record.answer ?? "",
            );
            return {
              id: normalizedId,
              no: Number(record.no ?? index + 1),
              keyword: keywordRaw,
              matchingWord: matchingRaw,
              imageKeywordFileName: getFileNameFromPath(
                String(record.image_keyword ?? ""),
              ),
              imageKeywordFile: null,
              imageKeywordUrl: buildUploadedMediaUrl(
                String(record.image_keyword ?? ""),
              ),
              audioKeywordFileName: getFileNameFromPath(
                String(record.sound_keyword ?? ""),
              ),
              audioKeywordFile: null,
              audioKeywordUrl: buildUploadedMediaUrl(
                String(record.sound_keyword ?? ""),
              ),
              imageMatchingFileName: getFileNameFromPath(
                String(record.image_matching_word ?? ""),
              ),
              imageMatchingFile: null,
              imageMatchingUrl: buildUploadedMediaUrl(
                String(record.image_matching_word ?? ""),
              ),
              audioMatchingFileName: getFileNameFromPath(
                String(record.sound_matching_word ?? ""),
              ),
              audioMatchingFile: null,
              audioMatchingUrl: buildUploadedMediaUrl(
                String(record.sound_matching_word ?? ""),
              ),
              hasHint: false,
              hintText: "",
              hintImageFileName: "",
              hintImageFile: null,
              hintImageUrl: null,
              hintAudioFileName: "",
              hintAudioFile: null,
              hintAudioUrl: null,
            };
          })
        : [createEmptyMatchingItem(1, 1), createEmptyMatchingItem(2, 2)];

    const skillsFromGameInfo = extractGameSkillsFromUnknown({
      skill_listening_speaking: gameInfo.skill_listening_speaking,
      skill_lang_nature: gameInfo.skill_lang_nature,
      skill_reading: gameInfo.skill_reading,
      skill_writing: gameInfo.skill_writing,
    });

    const firstRow = incomingRows[0] as Record<string, unknown> | undefined;
    const skillsFromRows = extractGameSkillsFromUnknown({
      skill_listening_speaking: firstRow?.skill_listening_speaking,
      skill_lang_nature: firstRow?.skill_lang_nature,
      skill_reading: firstRow?.skill_reading,
      skill_writing: firstRow?.skill_writing,
    });

    const resolvedSkills =
      incomingRows.length > 0 ? skillsFromRows : skillsFromGameInfo;
    setGameSkills(resolvedSkills);
    initialGameSkillsRef.current = resolvedSkills;

    const audiosFromGameInfo = extractGameAudiosFromUnknown({
      audio_question: gameInfo.audio_question,
      audio_choice: gameInfo.audio_choice,
      audio_assistant_voice: gameInfo.audio_assistant_voice,
      audio_background: gameInfo.audio_background,
    });

    const firstRowForAudio = incomingRows[0] as
      | Record<string, unknown>
      | undefined;
    const audiosFromRows = extractGameAudiosFromUnknown({
      audio_question: firstRowForAudio?.audio_question,
      audio_choice: firstRowForAudio?.audio_choice,
      audio_assistant_voice: firstRowForAudio?.audio_assistant_voice,
      audio_background: firstRowForAudio?.audio_background,
    });

    let resolvedAudios =
      incomingRows.length > 0 ? audiosFromRows : audiosFromGameInfo;
    if (incomingRows.length > 1) {
      for (let index = 1; index < incomingRows.length; index += 1) {
        const row = incomingRows[index] as Record<string, unknown>;
        const rowAudios = normalizeGameAudiosOrNull({
          audio_question: row.audio_question,
          audio_choice: row.audio_choice,
          audio_assistant_voice: row.audio_assistant_voice,
          audio_background: row.audio_background,
        });

        if (!rowAudios) {
          console.warn(
            `[FlipCards] Invalid audio values in row ${index + 1}, using first row values.`,
          );
          continue;
        }

        if (!areGameAudiosEqual(rowAudios, resolvedAudios)) {
          console.warn(
            "[FlipCards] Audio values are inconsistent across rows. Using the first row values.",
          );
          break;
        }
      }
    }

    setGameAudios(resolvedAudios);
    initialGameAudiosRef.current = resolvedAudios;

    const normalizedItems = mappedItems.map((item, index) => ({
      ...item,
      no: index + 1,
    }));
    setItems(normalizedItems);

    initialMatchingIdsRef.current = collectPersistedIdsFromApiRows(
      incomingRows,
      (row) => {
        const mappedId = Number((row as Record<string, unknown>).id);
        return Number.isInteger(mappedId) && mappedId > 0 ? mappedId : null;
      },
    );
    nextMatchingIdRef.current =
      normalizedItems.reduce((max, item) => Math.max(max, item.id), 0) + 1;
    setIsUpdateMode(incomingRows.length > 0);
    savedFormSnapshotRef.current = createMatchingItemsGameSnapshot(
      incomingExerciseName,
      incomingSuggestion,
      incomingDescription,
      resolvedSkills,
      resolvedAudios,
      normalizedItems,
    );
  };

  useEffect(() => {
    applyLoadedGameDetail(initialGameDetail);
  }, [initialGameDetail]);

  const setNoByOrder = (sourceItems: MatchingItem[]) =>
    sourceItems.map((item, index) => ({ ...item, no: index + 1 }));

  const updateGameSkill = (skillKey: GameSkillKey, checked: boolean) => {
    setGameSkills((previous) => ({
      ...previous,
      [skillKey]: checked,
    }));
  };

  const updateGameAudio = (audioKey: GameAudiosKey, checked: boolean) => {
    setGameAudios((previous) => ({
      ...previous,
      [audioKey]: checked,
    }));
  };

  const addItem = () => {
    if (!canAddItem) {
      return;
    }
    const newId = nextMatchingIdRef.current;
    nextMatchingIdRef.current += 1;
    setItems((previous) =>
      setNoByOrder([
        ...previous,
        createEmptyMatchingItem(newId, previous.length + 1),
      ]),
    );
  };

  const removeItem = async (id: number) => {
    if (!canRemoveItem) {
      return;
    }
    if (isUpdateMode && initialMatchingIdsRef.current.has(id)) {
      const deleteResponse = await matchingAPI.hardDeleteMatching({
        matchingId: id,
      });
      if (deleteResponse?.status === "failed") {
        setStatusPopUp({
          title: tFC("error"),
          message: tFC("deleteError"),
          type: "error",
        });
        return;
      }
      initialMatchingIdsRef.current.delete(id);
      patchSavedMatchingItemsSnapshotAfterItemRemoved(savedFormSnapshotRef, id);
    }
    setItems((previous) =>
      setNoByOrder(previous.filter((item) => item.id !== id)),
    );
  };

  const moveItem = (id: number, direction: -1 | 1) => {
    setItems((previous) => {
      const currentIndex = previous.findIndex((item) => item.id === id);
      if (currentIndex < 0) {
        return previous;
      }

      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }

      const reordered = [...previous];
      const [moved] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, moved);
      return setNoByOrder(reordered);
    });
  };

  const updateItem = (
    id: number,
    updater: (item: MatchingItem) => MatchingItem,
  ) => {
    setItems((previous) =>
      previous.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const updateTextField = (
    id: number,
    field: "keyword" | "matchingWord",
    value: string,
  ) => {
    if (
      fieldErrorHighlightKey === matchingItemFieldTarget(id, field).highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    updateItem(id, (item) => ({ ...item, [field]: value }));
  };

  const setFileOnItem = (
    id: number,
    options: {
      file: File;
      fileNameField:
        | "imageKeywordFileName"
        | "audioKeywordFileName"
        | "imageMatchingFileName"
        | "audioMatchingFileName"
        | "hintImageFileName"
        | "hintAudioFileName";
      fileField:
        | "imageKeywordFile"
        | "audioKeywordFile"
        | "imageMatchingFile"
        | "audioMatchingFile"
        | "hintImageFile"
        | "hintAudioFile";
      urlField:
        | "imageKeywordUrl"
        | "audioKeywordUrl"
        | "imageMatchingUrl"
        | "audioMatchingUrl"
        | "hintImageUrl"
        | "hintAudioUrl";
      forceEnableHint?: boolean;
    },
  ) => {
    updateItem(id, (item) => ({
      ...item,
      [options.fileNameField]: options.file.name,
      [options.fileField]: options.file,
      [options.urlField]: null,
      ...(options.forceEnableHint ? { hasHint: true } : {}),
    }));
  };

  const getDeleteFieldLabel = (field: FlipCardMediaDeleteField) => {
    switch (field) {
      case "image_keyword":
        return tFC("questionImage");
      case "sound_keyword":
        return tFC("questionAudio");
      case "image_matching_word":
        return tFC("matchImage");
      case "sound_matching_word":
        return tFC("matchAudio");
      default:
        return tFC("dataLabel");
    }
  };

  const requestDeleteMediaField = (
    itemId: number,
    field: FlipCardMediaDeleteField,
  ) => {
    setPendingMediaDelete({ itemId, field });
  };

  const clearMediaFieldFromItem = (
    itemId: number,
    field: FlipCardMediaDeleteField,
  ) => {
    updateItem(itemId, (item) => {
      if (field === "image_keyword") {
        return {
          ...item,
          imageKeywordFileName: "",
          imageKeywordFile: null,
          imageKeywordUrl: null,
        };
      }

      if (field === "sound_keyword") {
        return {
          ...item,
          audioKeywordFileName: "",
          audioKeywordFile: null,
          audioKeywordUrl: null,
        };
      }

      if (field === "image_matching_word") {
        return {
          ...item,
          imageMatchingFileName: "",
          imageMatchingFile: null,
          imageMatchingUrl: null,
        };
      }

      return {
        ...item,
        audioMatchingFileName: "",
        audioMatchingFile: null,
        audioMatchingUrl: null,
      };
    });
  };

  const confirmDeleteMediaField = async () => {
    if (!pendingMediaDelete) {
      return;
    }

    const { itemId, field } = pendingMediaDelete;
    const shouldCallDeleteApi =
      isUpdateMode && initialMatchingIdsRef.current.has(itemId);

    try {
      setIsDeletingMedia(true);

      if (shouldCallDeleteApi) {
        const response = await matchingAPI.hardDeleteMediaFileAndHint({
          matchingId: itemId,
          field,
        });

        if (response?.status === "failed") {
          setStatusPopUp({
            title: tFC("error"),
            message: `${tFC("delete")} ${getDeleteFieldLabel(field)} ${tFC("error")}`,
            type: "error",
          });
          return;
        }

        patchSavedMatchingItemsSnapshotAfterMediaDeleted(
          savedFormSnapshotRef,
          itemId,
          field,
        );
      }

      clearMediaFieldFromItem(itemId, field);
      setPendingMediaDelete(null);
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const collectUploadTargets = (sourceItems: MatchingItem[]) => {
    const targets: UploadTarget[] = [];

    sourceItems.forEach((item, index) => {
      const timestamp = Date.now();

      if (item.imageKeywordFile && !item.imageKeywordUrl) {
        const ext = getFileExtension(item.imageKeywordFile.name);
        targets.push({
          key: `m-${index}-image_keyword`,
          file: item.imageKeywordFile,
          fileName: `image_keyword_m${item.id}_${timestamp}${ext}`,
          sectionName: "image_keyword",
          refId: `matching_${item.id}`,
        });
      }

      if (item.audioKeywordFile && !item.audioKeywordUrl) {
        const ext = getFileExtension(item.audioKeywordFile.name);
        targets.push({
          key: `m-${index}-sound_keyword`,
          file: item.audioKeywordFile,
          fileName: `sound_keyword_m${item.id}_${timestamp}${ext}`,
          sectionName: "sound_keyword",
          refId: `matching_${item.id}`,
        });
      }

      if (item.imageMatchingFile && !item.imageMatchingUrl) {
        const ext = getFileExtension(item.imageMatchingFile.name);
        targets.push({
          key: `m-${index}-image_matching_word`,
          file: item.imageMatchingFile,
          fileName: `image_matching_word_m${item.id}_${timestamp}${ext}`,
          sectionName: "image_matching_word",
          refId: `matching_${item.id}`,
        });
      }

      if (item.audioMatchingFile && !item.audioMatchingUrl) {
        const ext = getFileExtension(item.audioMatchingFile.name);
        targets.push({
          key: `m-${index}-sound_matching_word`,
          file: item.audioMatchingFile,
          fileName: `sound_matching_word_m${item.id}_${timestamp}${ext}`,
          sectionName: "sound_matching_word",
          refId: `matching_${item.id}`,
        });
      }
    });

    return targets;
  };

  const uploadPendingMedia = async (targets: UploadTarget[]) => {
    const uploadedUrlByKey = new Map<string, string>();

    if (targets.length === 0) {
      return uploadedUrlByKey;
    }

    const uploadResults = await Promise.allSettled(
      targets.map((target) =>
        uploadAPI
          .uploadMedia({
            file: target.file,
            fileName: target.fileName,
            sectionName: target.sectionName,
            ref_id: target.refId,
          })
          .then((response) => ({ target, response })),
      ),
    );

    for (const result of uploadResults) {
      if (result.status === "rejected") {
        throw new Error("upload_failed");
      }

      const response = result.value.response as {
        status?: string;
        url?: string;
      };
      if (response?.status === "failed" || !response?.url) {
        throw new Error("upload_failed");
      }

      uploadedUrlByKey.set(result.value.target.key, response.url);
    }

    return uploadedUrlByKey;
  };

  const applyUploadedUrlsToState = (
    sourceItems: MatchingItem[],
    uploadedUrlByKey: Map<string, string>,
  ) => {
    return sourceItems.map((item, index) => ({
      ...item,
      imageKeywordUrl:
        uploadedUrlByKey.get(`m-${index}-image_keyword`) ??
        item.imageKeywordUrl,
      imageKeywordFile: uploadedUrlByKey.get(`m-${index}-image_keyword`)
        ? null
        : item.imageKeywordFile,
      audioKeywordUrl:
        uploadedUrlByKey.get(`m-${index}-sound_keyword`) ??
        item.audioKeywordUrl,
      audioKeywordFile: uploadedUrlByKey.get(`m-${index}-sound_keyword`)
        ? null
        : item.audioKeywordFile,
      imageMatchingUrl:
        uploadedUrlByKey.get(`m-${index}-image_matching_word`) ??
        item.imageMatchingUrl,
      imageMatchingFile: uploadedUrlByKey.get(`m-${index}-image_matching_word`)
        ? null
        : item.imageMatchingFile,
      audioMatchingUrl:
        uploadedUrlByKey.get(`m-${index}-sound_matching_word`) ??
        item.audioMatchingUrl,
      audioMatchingFile: uploadedUrlByKey.get(`m-${index}-sound_matching_word`)
        ? null
        : item.audioMatchingFile,
      hintImageUrl: null,
      hintImageFile: null,
      hintAudioUrl: null,
      hintAudioFile: null,
    }));
  };

  const refreshFromServer = async () => {
    if (!previewUuid) {
      return;
    }

    const latestGameDetail =
      await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
    if (
      latestGameDetail &&
      (latestGameDetail as { status?: string }).status !== "failed"
    ) {
      applyLoadedGameDetail(
        latestGameDetail as FlipCardsProps["initialGameDetail"],
      );
    }
  };

  const handleSubmitFlipCards = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFC("error"),
        message: tFC("noRecentGameData"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tFC("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tFC("error"),
            message: tFC("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      if (
        !Array.isArray(itemsWithResolvedUrls) ||
        itemsWithResolvedUrls.length === 0
      ) {
        openStatusPopUp(
          setStatusPopUp,
          createGameFieldErrorPopUp(
            tFC("error"),
            tFC("minMatchItems"),
            matchingItemFieldTarget(items[0]?.id ?? 1, "keyword"),
          ),
        );
        return;
      }

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tFC("error"),
          message: tFC("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tFC("error"),
          message: tFC("invalidAudioData"),
          type: "error",
        });
        return;
      }

      const seenNos = new Set<number>();
      for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
        const item = itemsWithResolvedUrls[index];
        const no = index + 1;

        if (!Number.isInteger(no)) {
          setStatusPopUp({
            title: tFC("error"),
            message: tFC("invalidItemOrder"),
            type: "error",
          });
          return;
        }

        if (seenNos.has(no)) {
          setStatusPopUp({
            title: tFC("error"),
            message: tFC("duplicateItemOrder"),
            type: "error",
          });
          return;
        }
        seenNos.add(no);

        const hasKeywordMedia =
          Boolean(item.keyword.trim()) ||
          Boolean(item.imageKeywordUrl) ||
          Boolean(item.audioKeywordUrl);
        if (!hasKeywordMedia) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tFC("error"),
              `${tFC("questionOrder")} ${no} ${tFC("minContentRequired")}`,
              matchingItemFieldTarget(item.id, "keyword"),
            ),
          );
          return;
        }

        const hasMatchingMedia =
          Boolean(item.matchingWord.trim()) ||
          Boolean(item.imageMatchingUrl) ||
          Boolean(item.audioMatchingUrl);
        if (!hasMatchingMedia) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tFC("error"),
              `${tFC("matchOrder")} ${no} ${tFC("minContentRequired")}`,
              matchingItemFieldTarget(item.id, "matchingWord"),
            ),
          );
          return;
        }
      }

      const payloadRows = itemsWithResolvedUrls.map((item, index) => ({
        no: index + 1,
        expected_time: expectedTimeValue,
        keyword: item.keyword.trim(),
        matching_word: item.matchingWord.trim(),
        hint: null,
        image_keyword: item.imageKeywordUrl || null,
        sound_keyword: item.audioKeywordUrl || null,
        image_matching_word: item.imageMatchingUrl || null,
        sound_matching_word: item.audioMatchingUrl || null,
        image_hint: null,
        sound_hint: null,
      }));

      const response = await matchingAPI.createMatching({
        gameId: createdGameData.id,
        skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
        skill_lang_nature: normalizedGameSkills.skill_lang_nature,
        skill_reading: normalizedGameSkills.skill_reading,
        skill_writing: normalizedGameSkills.skill_writing,
        audio_question: normalizedGameAudios.audio_question,
        audio_choice: normalizedGameAudios.audio_choice,
        audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
        audio_background: normalizedGameAudios.audio_background,
        matchings: payloadRows,
      });

      if (response?.status === "failed") {
        setStatusPopUp({
          title: tFC("error"),
          message: tFC("saveError"),
          type: "error",
        });
        return;
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      await refreshFromServer();
      setIsUpdateMode(true);
      setStatusPopUp(buildGameSuccessStatus(tFC("flipCards"), "create"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFlipCards = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFC("error"),
        message: tFC("noGameDataToUpdate"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tFC("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tFC("error"),
            message: tFC("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tFC("error"),
          message: tFC("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tFC("error"),
          message: tFC("invalidAudioData"),
          type: "error",
        });
        return;
      }

      const changedSkillPayload: Partial<GameSkills> = {};
      if (
        normalizedGameSkills.skill_listening_speaking !==
        initialGameSkillsRef.current.skill_listening_speaking
      ) {
        changedSkillPayload.skill_listening_speaking =
          normalizedGameSkills.skill_listening_speaking;
      }
      if (
        normalizedGameSkills.skill_lang_nature !==
        initialGameSkillsRef.current.skill_lang_nature
      ) {
        changedSkillPayload.skill_lang_nature =
          normalizedGameSkills.skill_lang_nature;
      }
      if (
        normalizedGameSkills.skill_reading !==
        initialGameSkillsRef.current.skill_reading
      ) {
        changedSkillPayload.skill_reading = normalizedGameSkills.skill_reading;
      }
      if (
        normalizedGameSkills.skill_writing !==
        initialGameSkillsRef.current.skill_writing
      ) {
        changedSkillPayload.skill_writing = normalizedGameSkills.skill_writing;
      }

      const changedAudioPayload: Partial<GameAudios> = {};
      if (
        normalizedGameAudios.audio_question !==
        initialGameAudiosRef.current.audio_question
      ) {
        changedAudioPayload.audio_question =
          normalizedGameAudios.audio_question;
      }
      if (
        normalizedGameAudios.audio_choice !==
        initialGameAudiosRef.current.audio_choice
      ) {
        changedAudioPayload.audio_choice = normalizedGameAudios.audio_choice;
      }
      if (
        normalizedGameAudios.audio_assistant_voice !==
        initialGameAudiosRef.current.audio_assistant_voice
      ) {
        changedAudioPayload.audio_assistant_voice =
          normalizedGameAudios.audio_assistant_voice;
      }
      if (
        normalizedGameAudios.audio_background !==
        initialGameAudiosRef.current.audio_background
      ) {
        changedAudioPayload.audio_background =
          normalizedGameAudios.audio_background;
      }

      const currentIds = new Set<number>(
        itemsWithResolvedUrls.map((item) => item.id),
      );
      const deletedIds = Array.from(initialMatchingIdsRef.current).filter(
        (id) => !currentIds.has(id),
      );

      for (const deletedId of deletedIds) {
        const deleteResponse = await matchingAPI.hardDeleteMatching({
          matchingId: deletedId,
        });
        if (deleteResponse?.status === "failed") {
          setStatusPopUp({
            title: tFC("error"),
            message: `${tFC("deleteItemError")} ${deletedId} ${tFC("error")}`,
            type: "error",
          });
          return;
        }
      }

      const existingRows = itemsWithResolvedUrls.filter((item) =>
        initialMatchingIdsRef.current.has(item.id),
      );
      const newRows = itemsWithResolvedUrls.filter(
        (item) => !initialMatchingIdsRef.current.has(item.id),
      );

      for (const row of existingRows) {
        const updateResponse = await matchingAPI.updateMatching({
          matchingId: row.id,
          no: row.no,
          expected_time: expectedTimeValue,
          keyword: row.keyword.trim(),
          matching_word: row.matchingWord.trim(),
          hint: null,
          image_keyword: row.imageKeywordUrl || null,
          sound_keyword: row.audioKeywordUrl || null,
          image_matching_word: row.imageMatchingUrl || null,
          sound_matching_word: row.audioMatchingUrl || null,
          image_hint: null,
          sound_hint: null,
          ...changedSkillPayload,
          ...changedAudioPayload,
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tFC("error"),
            message: `${tFC("updateItemOrderError")} ${row.no}`,
            type: "error",
          });
          return;
        }
      }

      if (newRows.length > 0) {
        const createNewRowsResponse = await matchingAPI.createMatching({
          gameId: createdGameData.id,
          skill_listening_speaking:
            normalizedGameSkills.skill_listening_speaking,
          skill_lang_nature: normalizedGameSkills.skill_lang_nature,
          skill_reading: normalizedGameSkills.skill_reading,
          skill_writing: normalizedGameSkills.skill_writing,
          audio_question: normalizedGameAudios.audio_question,
          audio_choice: normalizedGameAudios.audio_choice,
          audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
          audio_background: normalizedGameAudios.audio_background,
          matchings: newRows.map((row) => ({
            no: row.no,
            expected_time: expectedTimeValue,
            keyword: row.keyword.trim(),
            matching_word: row.matchingWord.trim(),
            hint: null,
            image_keyword: row.imageKeywordUrl || null,
            sound_keyword: row.audioKeywordUrl || null,
            image_matching_word: row.imageMatchingUrl || null,
            sound_matching_word: row.audioMatchingUrl || null,
            image_hint: null,
            sound_hint: null,
          })),
        });

        if (createNewRowsResponse?.status === "failed") {
          setStatusPopUp({
            title: tFC("error"),
            message: tFC("addItemError"),
            type: "error",
          });
          return;
        }
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      await refreshFromServer();

      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      setIsUpdateMode(true);
      setStatusPopUp(buildGameSuccessStatus(tFC("flipCards"), "update"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseStatusPopUp = () => {
    closeStatusPopUp(setStatusPopUp, () => {
      void onCompleted;
    });
  };

  const handleStatusButtonClick = () => {
    if (isStatusConfirming || isSubmitting) {
      return;
    }

    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFC("updateStatusError"),
        message: tFC("noGameDataToChangeStatus"),
        type: "error",
      });
      return;
    }

    setIsStatusConfirmOpen(true);
  };

  const closeStatusConfirm = () => {
    if (isStatusConfirming) {
      return;
    }
    setIsStatusConfirmOpen(false);
  };

  const confirmStatusToggle = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFC("updateStatusError"),
        message: tFC("noGameDataToChangeStatus"),
        type: "error",
      });
      setIsStatusConfirmOpen(false);
      return;
    }

    const nextStatus = !isPublished;
    const dialogContent = getStatusToggleDialogContent(
      isPublished,
      statusToggleDialogLabels,
    );

    setIsStatusConfirming(true);
    const response = await mainAPI.updateGameStatus({
      id: createdGameData.id,
      status: nextStatus,
    });
    setIsStatusConfirming(false);
    setIsStatusConfirmOpen(false);

    if (!response || response.status === "failed") {
      setStatusPopUp({
        title: tFC("updateStatusError"),
        message: tFC("changeStatusError"),
        type: "error",
      });
      return;
    }

    setIsPublished(nextStatus);
    setStatusPopUp({
      title: dialogContent.successTitle,
      message: dialogContent.successMessage,
      type: "success",
    });
  };

  const isExpectedTimeValid = useMemo(() => {
    const trimmed = expectedTime.trim();
    if (trimmed === "") {
      return false;
    }
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 0;
  }, [expectedTime]);
  const expectedTimeDisplay = useMemo(() => {
    if (!isExpectedTimeValid) {
      return "";
    }
    const value = Number(expectedTime.trim());
    if (value > 59) {
      const minutes = value / 60;
      const minuteText = Number.isInteger(minutes)
        ? String(minutes)
        : minutes.toFixed(2).replace(/\.?0+$/, "");
      return `${minuteText} ${tMain("expectedTimeMinuteUnit")}`;
    }
    return `${value} ${tMain("expectedTimeSecondUnit")}`;
  }, [expectedTime, isExpectedTimeValid, tMain]);
  const isSaveReady = useMemo(
    () => areMatchingItemsSaveReady(items) && isExpectedTimeValid,
    [items, isExpectedTimeValid],
  );

  const currentFormSnapshot = useMemo(
    () =>
      createMatchingItemsGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        items,
      ),
    [exerciseName, suggestion, description, gameSkills, gameAudios, items],
  );
  const hasUnsavedChanges = useHasUnsavedGameFormChanges(
    isUpdateMode,
    currentFormSnapshot,
    savedFormSnapshotRef,
  );
  useReportGameEditorLeaveGuard(
    getShouldConfirmLeaveGameEditor(isUpdateMode, hasUnsavedChanges),
  );
  const canPreview = getCanGamePreview(
    isUpdateMode,
    previewUuid,
    hasUnsavedChanges,
  );

  const handleClickFinishButton = () => {
    if (isSubmitting || !isSaveReady) {
      return;
    }
    setIsConfirmSaveOpen(true);
  };

  const handleCancelConfirmSave = () => {
    setIsConfirmSaveOpen(false);
  };

  const handleConfirmSave = () => {
    setIsConfirmSaveOpen(false);
    if (isUpdateMode) {
      void handleUpdateFlipCards();
      return;
    }
    void handleSubmitFlipCards();
  };

  const handleOpenPreview = () => {
    if (!canPreview || hasUnsavedChanges || !isExpectedTimeValid) {
      if (!isExpectedTimeValid) {
        setStatusPopUp({
          title: tFC("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
      } else if (hasUnsavedChanges) {
        setStatusPopUp({
          title: PREVIEW_UNSAVED_TITLE,
          message: PREVIEW_UNSAVED_MESSAGE,
          type: "info",
        });
      }
      return;
    }

    if (!previewUuid) {
      setStatusPopUp({
        title: tFC("previewError"),
        message: tFC("noGameUUIDForPreview"),
        type: "error",
      });
      return;
    }

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}?preview`;
    setPreviewIframeUrl(previewUrl);
  };

  const handleClosePreview = () => {
    setPreviewIframeUrl(null);
  };

  return (
    <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
      <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
        <span className="font-semibold text-[#4E4E4E]">
          {tFC("editContent")} &gt;{" "}
        </span>
        <span className="font-bold text-[#2b2b2b]">{tFC("flipCards")}</span>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
            {tFC("activityName")}
          </label>
          <input
            value={exerciseName}
            onChange={(event) => setExerciseName(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tFC("noActivityName")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tFC("details")}
          </label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tFC("selectCorrectAnswer")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tFC("questionTitle")}
          </label>
          <input
            value={suggestion}
            onChange={(event) => setSuggestion(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            // placeholder={tFC("dragAndMatch")}
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-lg font-medium text-[#5b5b5b]">
            {tFC("status")}:
          </span>
          <button
            type="button"
            onClick={handleStatusButtonClick}
            disabled={
              !createdGameData?.id || isSubmitting || isStatusConfirming
            }
            className={getPublishButtonClass(isPublished)}
          >
            {renderPublishStatusContent(isPublished, publishStatusLabels, 16)}
          </button>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto p-3">
        <div className="w-full min-w-[640px] space-y-3 rounded-md border border-[#e0e0e0] bg-[#f9f9f9] p-3">
          <div className="grid w-full grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_96px] items-center gap-3 pb-2">
            <span></span>
            <div className="flex flex-col items-start gap-1">
              {/* <span className="inline-block rounded-md mt-8 bg-[#67E52C] px-10 py-1.5 text-lg font-semibold text-white">ถูกต้อง</span> */}
              <span className="text-sm mt-1 font-semibold">
                {tFC("question")}
              </span>
            </div>
            <div className="flex flex-col items-start gap-1">
              {/* <span className="inline-block rounded-md mt-8 bg-[#F54043] px-10 py-1.5 text-lg font-semibold text-white">ไม่ถูกต้อง</span> */}
              <span className="text-sm mt-1 font-semibold">{tFC("match")}</span>
            </div>
            <span></span>
          </div>

          <div className="space-y-3">
            {items.map((item, index) => {
              const keywordImageId = `keyword-image-${item.id}`;
              const keywordAudioId = `keyword-audio-${item.id}`;
              const matchingImageId = `matching-image-${item.id}`;
              const matchingAudioId = `matching-audio-${item.id}`;
              const hintImageId = `hint-image-${item.id}`;
              const hintAudioId = `hint-audio-${item.id}`;

              return (
                <div key={item.id} className="flex flex-col">
                  <div className="grid w-full grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_96px] items-center gap-3">
                    <span className="shrink-0 text-lg font-semibold text-[#5a5a5a] text-center">
                      {index + 1}.
                    </span>

                    <div className="relative min-w-0">
                      <div
                        id={
                          matchingItemFieldTarget(item.id, "keyword").scrollId
                        }
                        className={`flex h-9.5 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(matchingItemFieldTarget(item.id, "keyword").highlightKey)}`}
                      >
                        <input
                          value={item.keyword}
                          onChange={(event) =>
                            updateTextField(
                              item.id,
                              "keyword",
                              event.target.value,
                            )
                          }
                          className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                        />
                        <div className="flex items-center gap-1 pl-2">
                          <MediaEditGroup
                            imageInputId={keywordImageId}
                            imageFile={item.imageKeywordFile}
                            imageUrl={item.imageKeywordUrl}
                            imageAlt={tFC("questionImage")}
                            onImageChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "imageKeywordFileName",
                                fileField: "imageKeywordFile",
                                urlField: "imageKeywordUrl",
                              })
                            }
                            audioInputId={keywordAudioId}
                            audioFile={item.audioKeywordFile}
                            audioUrl={item.audioKeywordUrl}
                            onAudioChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "audioKeywordFileName",
                                fileField: "audioKeywordFile",
                                urlField: "audioKeywordUrl",
                              })
                            }
                            onImageDelete={() =>
                              requestDeleteMediaField(item.id, "image_keyword")
                            }
                            onAudioDelete={() =>
                              requestDeleteMediaField(item.id, "sound_keyword")
                            }
                            disableDelete={isSubmitting || isDeletingMedia}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="relative min-w-0">
                      <div
                        id={
                          matchingItemFieldTarget(item.id, "matchingWord")
                            .scrollId
                        }
                        className={`flex h-9.5 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(matchingItemFieldTarget(item.id, "matchingWord").highlightKey)}`}
                      >
                        <input
                          value={item.matchingWord}
                          onChange={(event) =>
                            updateTextField(
                              item.id,
                              "matchingWord",
                              event.target.value,
                            )
                          }
                          className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                        />
                        <div className="flex items-center gap-1 pl-2">
                          <MediaEditGroup
                            imageInputId={matchingImageId}
                            imageFile={item.imageMatchingFile}
                            imageUrl={item.imageMatchingUrl}
                            imageAlt={tFC("matchImage")}
                            onImageChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "imageMatchingFileName",
                                fileField: "imageMatchingFile",
                                urlField: "imageMatchingUrl",
                              })
                            }
                            audioInputId={matchingAudioId}
                            audioFile={item.audioMatchingFile}
                            audioUrl={item.audioMatchingUrl}
                            onAudioChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "audioMatchingFileName",
                                fileField: "audioMatchingFile",
                                urlField: "audioMatchingUrl",
                              })
                            }
                            onImageDelete={() =>
                              requestDeleteMediaField(
                                item.id,
                                "image_matching_word",
                              )
                            }
                            onAudioDelete={() =>
                              requestDeleteMediaField(
                                item.id,
                                "sound_matching_word",
                              )
                            }
                            disableDelete={isSubmitting || isDeletingMedia}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex w-full min-w-0 items-center justify-center gap-3 self-start">
                      <div className="flex shrink-0 flex-col items-center gap-0 text-[#555]">
                        <button
                          type="button"
                          onClick={() => moveItem(item.id, -1)}
                          disabled={index === 0}
                          className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                          title={tFC("moveUp")}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(item.id, 1)}
                          disabled={index === items.length - 1}
                          className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                          title={tFC("moveDown")}
                        >
                          ↓
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteItemId(item.id)}
                        disabled={!canRemoveItem}
                        className={`${MEDIA_BTN_DELETE_MD} disabled:cursor-not-allowed disabled:opacity-50`}
                        title={
                          canRemoveItem
                            ? tFC("delete")
                            : `${tFC("minRequired")} ${MIN_ITEMS} ${tFC("item")}`
                        }
                      >
                        <MdDeleteOutline size={22} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="mt-5 ml-3">
              <button
                type="button"
                onClick={addItem}
                //disabled={!canAddItem}
                className="ml-0 md:ml-10 rounded border border-[#c2c2c2] bg-[#e6e6e6] px-4 py-1.5 text-lg font-medium text-[#444] hover:bg-[#d6d6d6] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
              >
                {tFC("addItem")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 w-full p-3">
        <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
          {tMain("expectedTimeLabel")}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={expectedTime}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (/^\d*$/.test(nextValue)) {
                setExpectedTime(nextValue);
              }
            }}
            required
            inputMode="numeric"
            className="h-10 w-full max-w-[360px] rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tMain("expectedTimePlaceholder")}
          />
          {expectedTimeDisplay ? (
            <span className="text-base font-semibold text-[#4a4a4a]">
              {expectedTimeDisplay}
            </span>
          ) : null}
        </div>
      </div>

      <div className="w-full p-3">
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <TbCategory2 className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tFC("competency")}
                </div>
                <div className="text-sm text-[#888]">
                  {tFC("competencySubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-stretch">
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <GiBrain className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFC("listeningSpeakingThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_listening_speaking}
                  onChange={(event) =>
                    updateGameSkill(
                      "skill_listening_speaking",
                      event.target.checked,
                    )
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlineBookOpen className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFC("readingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_reading}
                  onChange={(event) =>
                    updateGameSkill("skill_reading", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <MdOutlineChat className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFC("understandingThaiLanguage")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_lang_nature}
                  onChange={(event) =>
                    updateGameSkill("skill_lang_nature", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlinePencilAlt className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFC("writingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_writing}
                  onChange={(event) =>
                    updateGameSkill("skill_writing", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <HiOutlineVolumeUp className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tFC("sound")}
                </div>
                <div className="text-sm text-[#888]">
                  {tFC("soundSubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <HiOutlineMicrophone className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tFC("narration")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_question}
                      onChange={(event) =>
                        updateGameAudio("audio_question", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFC("muteNarration")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_choice}
                      onChange={(event) =>
                        updateGameAudio("audio_choice", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFC("muteOptionNarration")}
                  </label>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <IoGameControllerOutline className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tFC("gameSound")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_assistant_voice}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_assistant_voice",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFC("muteAssistant")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_background}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_background",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFC("muteBackground")}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        {isUpdateMode && previewUuid ? (
          <button
            type="button"
            onClick={handleOpenPreview}
            disabled={hasUnsavedChanges || isSubmitting || !isExpectedTimeValid}
            className={GAME_PREVIEW_BUTTON_CLASS}
            title={
              hasUnsavedChanges ? PREVIEW_BUTTON_DISABLED_TITLE : tFC("preview")
            }
          >
            {tFC("preview")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleClickFinishButton}
          disabled={isSubmitting || !isSaveReady}
          className="cursor-pointer rounded bg-[#f4d735] px-7 py-2 text-lg font-semibold text-[#4a4a4a] shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#e0c91a] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? tFC("saving") : tFC("save")}
        </button>
      </div>

      {isConfirmSaveOpen ? (
        <ConfirmPopUp
          title={tFC("confirmSave")}
          message={tFC("savePrompt")}
          cancelText={tFC("cancel")}
          confirmText={tFC("confirm")}
          onCancel={handleCancelConfirmSave}
          onConfirm={handleConfirmSave}
          isConfirming={isSubmitting}
        />
      ) : null}

      {isStatusConfirmOpen ? (
        <ConfirmPopUp
          title={
            getStatusToggleDialogContent(isPublished, statusToggleDialogLabels)
              .confirmTitle
          }
          message={
            getStatusToggleDialogContent(isPublished, statusToggleDialogLabels)
              .confirmMessage
          }
          confirmText={tFC("confirm")}
          cancelText={tFC("cancel")}
          onConfirm={() => {
            void confirmStatusToggle();
          }}
          onCancel={closeStatusConfirm}
          isConfirming={isStatusConfirming}
        />
      ) : null}

      {pendingDeleteItemId !== null ? (
        <ConfirmPopUp
          title={tFC("confirmDelete")}
          message={tFC("deletePrompt")}
          cancelText={tFC("cancel")}
          confirmText={tFC("confirm")}
          onCancel={() => setPendingDeleteItemId(null)}
          onConfirm={async () => {
            await removeItem(pendingDeleteItemId);
            setPendingDeleteItemId(null);
          }}
        />
      ) : null}

      {pendingMediaDelete !== null ? (
        <ConfirmPopUp
          title={tFC("confirmDelete")}
          message={`${tFC("wantToDelete")} ${getDeleteFieldLabel(pendingMediaDelete.field)} ${tFC("or")}`}
          cancelText={tFC("cancel")}
          confirmText={tFC("confirm")}
          onCancel={() => {
            if (isDeletingMedia) {
              return;
            }
            setPendingMediaDelete(null);
          }}
          onConfirm={() => {
            void confirmDeleteMediaField();
          }}
          isConfirming={isDeletingMedia}
        />
      ) : null}

      {statusPopUp ? (
        <StatusPopUp
          title={statusPopUp.title}
          message={statusPopUp.message}
          type={statusPopUp.type}
          onClose={handleCloseStatusPopUp}
        />
      ) : null}

      {previewIframeUrl ? (
        <GamePreviewModal
          src={previewIframeUrl}
          iframeTitle={`${tFC("flipCards")} ${tFC("preview")}`}
          onClose={handleClosePreview}
          closeVariant="danger"
          shellVariant="editor"
        />
      ) : null}
    </section>
  );
}
